import { TFunction } from 'i18next';

import { decode } from '@vestico/utils';

import { ClientReviewResponse, FitAnswer, LengthAnswer, UserModel, getDefaultHeightUnit } from '@vestico/model';

import { heightString } from './matchingStrings';

export const PreviewTextTypes = ['review', 'size', 'height', 'length', 'fit'] as const;
export type PreviewTextType = (typeof PreviewTextTypes)[number];

const createPreviewTextSize = (t: TFunction, response: ClientReviewResponse) => {
  const { products } = response;
  const firstVariant = products.length === 1 ? products[0] : undefined;
  const size = firstVariant && firstVariant.size?.trim();
  if (size && size.length > 0) {
    return t('LOC_PREVIEW_SIZE_WORN', { size, ns: 'shared' });
  }
  return undefined;
};

const createPreviewTextHeight = (t: TFunction, response: ClientReviewResponse, user: UserModel | null, i18nLanguage: string | undefined) => {
  const height = heightString(response, user?.heightUnit ?? getDefaultHeightUnit(i18nLanguage));
  if (height) {
    return t('LOC_PREVIEW_HEIGHT', { height, ns: 'shared' });
  }
  return undefined;
};

const createPreviewTextLengthAnswer = (t: TFunction, response: ClientReviewResponse) => {
  const { lengthAnswer } = response;
  if (lengthAnswer !== undefined) {
    switch (lengthAnswer) {
      case LengthAnswer.none:
        break;
      case LengthAnswer.littleLong:
        return t('LOC_PREVIEW_LENGTH_LITTLE_LONG', { ns: 'shared' });
      case LengthAnswer.long:
      case LengthAnswer.veryLong:
        return t('LOC_PREVIEW_LENGTH_LONG', { ns: 'shared' });
      case LengthAnswer.littleShort:
        return t('LOC_PREVIEW_LENGTH_LITTLE_SHORT', { ns: 'shared' });
      case LengthAnswer.short:
      case LengthAnswer.veryShort:
        return t('LOC_PREVIEW_LENGTH_SHORT', { ns: 'shared' });
      case LengthAnswer.normal:
        return t('LOC_PREVIEW_LENGTH_NORMAL', { ns: 'shared' });
    }
  }
  return undefined;
};

const createPreviewTextFitAnswer = (t: TFunction, response: ClientReviewResponse) => {
  const { fitAnswer } = response;
  if (fitAnswer !== undefined) {
    switch (fitAnswer) {
      case FitAnswer.none:
        break;
      case FitAnswer.littleLoose:
        return t('LOC_PREVIEW_FIT_LITTLE_LOOSE', { ns: 'shared' });
      case FitAnswer.loose:
      case FitAnswer.veryLoose:
        return t('LOC_PREVIEW_FIT_LOOSE', { ns: 'shared' });
      case FitAnswer.littleTight:
        return t('LOC_PREVIEW_FIT_LITTLE_TIGHT', { ns: 'shared' });
      case FitAnswer.tight:
      case FitAnswer.veryTight:
        return t('LOC_PREVIEW_FIT_TIGHT', { ns: 'shared' });
      case FitAnswer.normal:
        return t('LOC_PREVIEW_FIT_PERFECT', { ns: 'shared' });
    }
  }
  return undefined;
};

const createPreviewTextForType = (
  type: PreviewTextType,
  t: TFunction,
  response: ClientReviewResponse,
  user: UserModel | null,
  i18nLanguage: string | undefined
) => {
  switch (type) {
    case 'review':
      return undefined;
    case 'size':
      return createPreviewTextSize(t, response);
    case 'height':
      return createPreviewTextHeight(t, response, user, i18nLanguage);
    case 'length':
      return createPreviewTextLengthAnswer(t, response);
    case 'fit':
      return createPreviewTextFitAnswer(t, response);
  }
  throw new Error(`Unhandled type ${type}`);
};

export const generatePreviewText = (
  response: ClientReviewResponse,
  user: UserModel | null,
  t: TFunction,
  i18nLanguage: string | undefined
): string | undefined => {
  const { reviewText } = response;

  if (reviewText && reviewText.trim().length > 0) {
    return reviewText;
  }

  // We unhash the document id into n-1 different types (-1 because of review text)
  // Then take the one with the highest value

  const unhashedDocIDValues = decode(response.postDocumentID, PreviewTextTypes.length - 1);
  const mapping = new Map<PreviewTextType, number>();
  unhashedDocIDValues.forEach((value, index) => {
    mapping.set(PreviewTextTypes[index + 1], value);
  });

  // Sort preview text types by hash value descending
  const typesSortedByPreference = [...PreviewTextTypes].sort((a, b) => {
    const aValue = mapping.get(a);
    const bValue = mapping.get(b);
    if (aValue === undefined) {
      return 1;
    }
    if (bValue === undefined || aValue >= bValue) {
      return -1;
    }
    return 1;
  });

  for (const type of typesSortedByPreference) {
    const text = createPreviewTextForType(type, t, response, user, i18nLanguage);
    if (text) {
      return text;
    }
  }

  // Return an empty string so that username and more button still show up
  return '';
};
